import { WRITE_ROLES, type MemberRole } from './constants.js';

/**
 * Who may change whose membership. Pure functions, shared by the API and the web
 * app so the drawer greys out exactly the buttons the server would refuse — the
 * server still checks, the client only avoids offering a doomed action.
 */

/**
 * Inviting a write-capable role is owner-only: a partner must not be able to
 * mint another writer, which would let one invited account quietly widen the
 * set of people who can change the wedding.
 */
export function canInvite(callerRole: MemberRole, targetRole: MemberRole): boolean {
  // Ownership is established at workspace creation and transferred by a separate
  // flow, never handed out by an invite.
  if (targetRole === 'owner') return false;
  if (callerRole === 'owner') return true;
  if (callerRole === 'partner') return !WRITE_ROLES.includes(targetRole);
  return false;
}

/** Only the owner re-roles anyone, and never into or out of `owner`. */
export function canChangeRole(callerRole: MemberRole, targetRole: MemberRole): boolean {
  return callerRole === 'owner' && targetRole !== 'owner';
}

/** Anyone may leave; only the owner removes someone else. The owner cannot be removed. */
export function canRemoveMember(
  callerRole: MemberRole,
  targetRole: MemberRole,
  isSelf: boolean,
): boolean {
  if (targetRole === 'owner') return false;
  return isSelf || callerRole === 'owner';
}

/** Roles `callerRole` is allowed to hand out, for populating a role picker. */
export function invitableRoles(callerRole: MemberRole, all: readonly MemberRole[]): MemberRole[] {
  return all.filter((r) => canInvite(callerRole, r));
}
